import { create } from 'zustand';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { getCurrentSchoolId } from '../../lib/auth/schoolContext';

// Centralized realtime data store for Firestore collections
// Tracks: students, interventions, and derived maps
export const useDataStore = create((set, get) => ({
  // State
  students: [],
  studentsLoading: false,
  studentsError: null,

  interventions: [],
  interventionsByStudent: {},
  interventionsLoading: false,
  interventionsError: null,

  // Unsubscribe handlers
  _unsubs: {},

  // Actions
  subscribeStudents: () => {
    // Guard against duplicate subscriptions
    const { _unsubs } = get();
    if (_unsubs.students) {
      console.log('DataStore: Students subscription already exists, reusing');
      // Return no-op so callers can safely cleanup without affecting the shared listener
      return () => {};
    }

    console.log('DataStore: Starting students subscription');
    set({ studentsLoading: true, studentsError: null });
    let rawUnsub = null;
    (async () => {
      try {
        const schoolId = await getCurrentSchoolId();
        const colRef = collection(db, 'students');
        const q = query(colRef, where('schoolId', '==', schoolId));
        rawUnsub = onSnapshot(
          q,
          (snap) => {
            const students = snap.docs.map((doc) => {
              const raw = doc.data() || {};
              const firstName = raw.firstName || '';
              const lastName = raw.lastName || '';
              const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
              return {
                id: doc.id,
                ...raw,
                fullName,
                name: raw.name || fullName || 'Unknown Student',
              };
            });
            // Sort by lastName on the client side
            students.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));
            console.log('DataStore: Students loaded successfully', students.length, 'students');
            set({ students, studentsLoading: false });
          },
          (error) => {
            console.error('DataStore: Students subscription error', error);
            set({ studentsError: error, studentsLoading: false });
          }
        );
        // Wrap unsubscribe so it also clears the registry entry
        const wrappedUnsub = () => {
          try { rawUnsub && rawUnsub(); } catch { /* noop */ }
          set((state) => {
            const next = { ...state._unsubs };
            delete next.students;
            return { _unsubs: next };
          });
        };
        set((state) => ({ _unsubs: { ...state._unsubs, students: wrappedUnsub } }));
      } catch (e) {
        console.error('DataStore: Failed to start students subscription', e);
        set({ studentsError: e, studentsLoading: false });
      }
    })();
    return () => {
      try { rawUnsub && rawUnsub(); } catch { /* noop */ }
    };
  },

  subscribeInterventions: () => {
    const { _unsubs } = get();
    if (_unsubs.interventions) {
      // Return no-op so duplicate callers don't tear down the shared listener
      return () => {};
    }

    set({ interventionsLoading: true, interventionsError: null });
    let rawUnsub = null;
    (async () => {
      try {
        const schoolId = await getCurrentSchoolId();
        const colRef = collection(db, 'interventions');
        const q = query(colRef, where('schoolId', '==', schoolId));
        rawUnsub = onSnapshot(
          q,
          (snap) => {
            const interventions = snap.docs.map((doc) => ({ id: doc.id, ...(doc.data() || {}) }));
            // Build derived map of interventions per student id
            const byStudent = {};
            for (const i of interventions) {
              const ids = Array.isArray(i.students) ? i.students : [];
              for (const sid of ids) {
                if (!byStudent[sid]) byStudent[sid] = [];
                byStudent[sid].push({ id: i.id, name: i.name || 'Intervention', type: i.type || '' });
              }
            }
            set({ interventions, interventionsByStudent: byStudent, interventionsLoading: false });
          },
          (error) => set({ interventionsError: error, interventionsLoading: false })
        );
        const wrappedUnsub = () => {
          try { rawUnsub && rawUnsub(); } catch { /* noop */ }
          set((state) => {
            const next = { ...state._unsubs };
            delete next.interventions;
            return { _unsubs: next };
          });
        };
        set((state) => ({ _unsubs: { ...state._unsubs, interventions: wrappedUnsub } }));
      } catch (e) {
        set({ interventionsError: e, interventionsLoading: false });
      }
    })();
    return () => { try { rawUnsub && rawUnsub(); } catch { /* noop */ } };
  },

  // Cleanup all subscriptions
  unsubscribeAll: () => {
    const { _unsubs } = get();
    Object.values(_unsubs).forEach((fn) => {
      try { fn && fn(); } catch { /* noop */ }
    });
    // Clear registry and reset loading flags so UI doesn't get stuck
    set({
      _unsubs: {},
      studentsLoading: false,
      interventionsLoading: false,
    });
  },
}));
